import React, { useState } from 'react';
import { Calendar, Clock, Edit, Trash2, CheckCircle, Circle, Tag } from 'lucide-react';
import { TimelineEvent } from '../../types/invitation';
import { cn } from '../../lib/utils';
import TimelineNode from './TimelineNode';

interface TimelineItemProps {
  event: TimelineEvent;
  onEdit: (event: TimelineEvent) => void;
  onToggle: (eventId: string, completed: boolean) => void;
  onDelete: (eventId: string) => void;
}

const TimelineItem: React.FC<TimelineItemProps> = ({
  event,
  onEdit,
  onToggle,
  onDelete
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const getNodeType = (event: TimelineEvent) => {
    if (event.is_completed) return 'completed';
    
    const eventDate = new Date(event.target_date);
    const now = new Date();
    
    if (eventDate < now) return 'overdue';
    return 'pending';
  };
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-TW', {
      month: 'short',
      day: 'numeric',
      weekday: 'short'
    });
  };
  
  const getDaysUntil = (dateString: string) => {
    const eventDate = new Date(dateString);
    const now = new Date();
    const diffTime = eventDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return '今天';
    if (diffDays === 1) return '明天';
    if (diffDays === -1) return '昨天';
    if (diffDays < 0) return `${Math.abs(diffDays)} 天前`;
    return `${diffDays} 天後`;
  };
  
  return (
    <div 
      className="timeline-item relative mb-6"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* 時間節點 */}
      <TimelineNode
        type={getNodeType(event)}
      />
      
      {/* 任務內容 */}
      <div className="relative ml-8 bg-white rounded-lg border border-wedding-pink/20 p-4 shadow-sm hover:shadow-md transition-all duration-200">
        {/* 標題行 */}
        <div className="flex items-start justify-between mb-2">
          <h4 
            className={cn(
              "font-semibold text-wedding-text-title cursor-pointer hover:text-wedding-pink transition-colors",
              event.is_completed && "line-through text-gray-500"
            )}
            onClick={() => onEdit(event)}
          >
            {event.title}
          </h4>
          
          {/* 操作按鈕 */}
          <div className={cn(
            "flex items-center gap-1 transition-opacity duration-200",
            isHovered ? "opacity-100" : "opacity-0"
          )}>
            <button
              onClick={() => onToggle(event.id, !event.is_completed)}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title={event.is_completed ? "標記為未完成" : "標記為已完成"}
            >
              {event.is_completed ? 
                <CheckCircle className="w-4 h-4 text-green-500" /> :
                <Circle className="w-4 h-4 text-wedding-pink" />
              }
            </button>
            <button
              onClick={() => onEdit(event)}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="編輯任務"
            >
              <Edit className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={() => {
                const confirmDelete = window.confirm('確定要刪除這個任務嗎？此操作無法復原。');
                if (confirmDelete) {
                  onDelete(event.id);
                }
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="刪除任務"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        </div>
        
        {/* 日期和時間信息 */}
        <div className="flex items-center gap-4 text-sm text-wedding-text-body mb-2">
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            <span>{formatDate(event.target_date)}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span className={cn(
              getNodeType(event) === 'overdue' && "text-red-500 font-medium"
            )}>
              {getDaysUntil(event.target_date)}
            </span>
          </div>
        </div>
        
        {/* 描述 */}
        {event.description && (
          <p className="text-sm text-wedding-text-body mb-2 line-clamp-2">
            {event.description}
          </p>
        )}
        
        {/* 分類標籤 */}
        {event.category && (
          <div className="flex items-center gap-1">
            <Tag className="w-3 h-3 text-wedding-pink" />
            <span className="text-xs bg-wedding-pink/10 text-wedding-pink px-2 py-1 rounded-full">
              {event.category}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimelineItem;